import React from 'react';
import ReactDOM from 'react-dom/client';

import App from './App';
import { KeyCloakService } from '@inform-appshell/fusion-kit-keycloak';
import {
  AppServiceProvider,
  ConfigurationManagerBuilder,
  ConsoleLogger,
  EncryptedSessionStorage,
  FusionAppBuilder,
} from '@inform-appshell/fusion-kit-core';
import { LoggerOptions } from '@inform-appshell/fusion-kit-contracts';
import { DependenciesContext, ApplicationStateService } from '@inform-appshell/fusion-kit-react';
import { FederationModuleManager, RemoteModuleHealthService } from '@inform-appshell/fusion-kit-module-federation';
import { I18nService } from './services/I18nService';
import { resolveRealm } from './utils/resolveRealm';
import { resolveShellEmbed } from './utils/resolveEmbedParams';
import { LoadingScreen } from '@inform-appshell/mui-appshell';
import { MissingRealmError } from './components/MissingRealmError';
import { RealmNotConnectedError, StartupFailure } from './components/StartupError';
import { ShellProviders } from './components/ShellProviders';
import i18n from './i18n';

//******** create root element
const root = ReactDOM.createRoot(document.getElementById('root')!);

//******** pre-apply embedded theme before React mounts to avoid a flash (issue #396).
// Key/attribute mirror shell/hooks/useTheme.ts, which reads this as its initial theme.
// The shell owns `data-mui-color-scheme` for the document now that it is MUI (ADR-0023).
const embed = resolveShellEmbed();
if (embed.embedded !== undefined && embed.theme) {
  localStorage.setItem('cuam-theme', embed.theme);
  document.documentElement.dataset.muiColorScheme = embed.theme;
}

//******** check realm early — required URL parameter, no fallback
const realm = resolveRealm();
if (!realm) {
  console.error(
    `[CUAM] Missing required "realm" query parameter.\n\n` +
      `CUAM is multi-realm and requires the target realm in the URL.\n\n` +
      `  Current URL:  ${window.location.href}\n` +
      `  Expected:     ${window.location.origin}${window.location.pathname}?realm=<realm-name>\n\n` +
      `If you are integrating CUAM, ensure all links include the "realm" query parameter.\n` +
      `There is no default realm.`
  );
  root.render(
    <ShellProviders>
      <MissingRealmError />
    </ShellProviders>
  );
} else {
  // build() runs the auth factory, so bootstrap failures surface here, not in auth.init().
  await startApp(encodeURIComponent(realm)).catch((err: unknown) => renderStartupError(err, realm));
}

async function startApp(realm: string) {
  // Loader while auth/config bootstrap runs (may include a Keycloak redirect round-trip).
  // i18n is not ready this early, so the copy stays the product name.
  root.render(
    <ShellProviders>
      <LoadingScreen fullscreen message="Corporate User Access Management" />
    </ShellProviders>
  );

  //******** Manage configuration files
  const baseUrl = import.meta.env.VITE_BASE_URL ?? '/';
  const configurationManager = await new ConfigurationManagerBuilder()
    .withConfigurationDirectory(window.location.origin + baseUrl + 'config/')
    .withFileToLoad('config.json', 'config')
    .withFileToLoad('dremotes.json', 'modules')
    .build();

  //******** initialize auth service factory
  const authServiceFactory = async () => {
    const cuamUrl = import.meta.env.VITE_CUAM_URL ?? window.location.origin;
    const configUrl = `${cuamUrl}${baseUrl}api/realms/${realm}/config`;
    const configResponse = await fetch(configUrl);
    if (configResponse.status === 404) throw new RealmNotConnectedError(`No CUAM configuration for realm ${realm}`);
    if (!configResponse.ok) throw new Error(`GET ${configUrl} failed with HTTP ${configResponse.status}`);
    const keycloakConfig = await configResponse.json();

    // No redirectUri: keycloak-js then uses location.href at login/logout time, after init() has
    // stripped the OIDC callback params — keeping deep links and ?realm=/embed params (#536).
    return new KeyCloakService({
      url: keycloakConfig.url,
      realm,
      clientId: keycloakConfig.clientid,
      extraScopes: ['groups'],
    });
  };

  //******** initialize fusion app
  const fusionApp = await new FusionAppBuilder()
    .withName('cuam')
    .withAuthFactory(authServiceFactory)
    .withConfigManager(configurationManager)
    .withLogger(new ConsoleLogger(LoggerOptions.DEBUG))
    // ESS obfuscates sessionStorage values and prefixes keys for MFE isolation.
    // This key ships in the JS bundle — not a true secret. Consider sourcing
    // it from the backend config endpoint in the future (as Verticals do).
    .withEncryptedStorage(new EncryptedSessionStorage('cuam-ess-v1'))
    .withRemoteManager(new FederationModuleManager(new RemoteModuleHealthService())) //new RemoteModuleHealthService() we need this line of code for the chrome devtools
    .withServiceProvider(new AppServiceProvider())
    .build();

  //******** register application services
  const appState = new ApplicationStateService(fusionApp);
  // The service restores its state from session storage, and the theme hooks prefer that over the
  // `cuam-theme` key seeded above — so a URL theme must also go into the state, or a theme toggled
  // earlier in this tab wins.
  if (embed.embedded !== undefined && embed.theme) appState.set('theme', embed.theme);
  fusionApp.serviceProvider.register(appState);
  fusionApp.serviceProvider.register(new I18nService(i18n));

  //******** start the app
  const authenticated = await fusionApp?.auth.init();

  if (!authenticated) {
    await fusionApp.auth.login();
  } else {
    root.render(
      <React.StrictMode>
        <DependenciesContext value={{ fusionApp, isStandAlone: true }}>
          <ShellProviders>
            <App />
          </ShellProviders>
        </DependenciesContext>
      </React.StrictMode>
    );
  }
}

function renderStartupError(err: unknown, realm: string) {
  console.error('[CUAM] Startup failed:', err);
  root.render(
    <ShellProviders>
      <StartupFailure error={err} realm={realm} />
    </ShellProviders>
  );
}
